import type { WooStoreConfig } from "../types";
import { normalizeWordPressSiteOrigin } from "../utils/wordpressSiteBase";

const ORGANIZATION_LOGO_URL =
  "https://fluxmed.cloud/StructureDefinition/OrganizationLogo";

/** Entrada por CNPJ (institutionId) — lida de `VITE_WOO_ORGANIZATION_STORES`. */
export type OrganizationStoreEnvEntry = {
  /** slug interno estável (ex.: plant-based-labs). Default: institutionId. */
  storeSlug?: string;
  /** Base Woo/WordPress — pode incluir `/wp-json/wc/v3`. Obrigatório (ou via siteOriginEnv). */
  siteOrigin?: string;
  /** Nome de outra env Vite cujo valor é a base WordPress. */
  siteOriginEnv?: string;
};

export type OrganizationStoreRegistry = Record<string, OrganizationStoreEnvEntry>;

type ParsedRegistry = {
  registry: OrganizationStoreRegistry;
  configError: string | null;
};

function readEnv(key: string): string {
  return (import.meta.env[key as keyof ImportMetaEnv] as string | undefined) ?? "";
}

function parseOrganizationStoreRegistry(): ParsedRegistry {
  const raw = readEnv("VITE_WOO_ORGANIZATION_STORES").trim();

  if (!raw) {
    return { registry: {}, configError: null };
  }

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {
        registry: {},
        configError:
          "Configuração inválida: VITE_WOO_ORGANIZATION_STORES deve ser um objeto JSON (CNPJ → config).",
      };
    }
    return { registry: parsed as OrganizationStoreRegistry, configError: null };
  } catch {
    return {
      registry: {},
      configError:
        "Configuração inválida: não foi possível interpretar VITE_WOO_ORGANIZATION_STORES.",
    };
  }
}

const PARSED_REGISTRY = parseOrganizationStoreRegistry();

function resolveSiteOrigin(entry: OrganizationStoreEnvEntry): string | null {
  if (entry.siteOrigin?.trim()) {
    return normalizeWordPressSiteOrigin(entry.siteOrigin.trim());
  }
  if (entry.siteOriginEnv?.trim()) {
    const baseUrl = readEnv(entry.siteOriginEnv.trim());
    if (baseUrl) {
      return normalizeWordPressSiteOrigin(baseUrl);
    }
  }
  return null;
}

/** Erro de parse do JSON de env — exibir tela de erro no marketplace. */
export function getMarketplaceRegistryConfigError(): string | null {
  return PARSED_REGISTRY.configError;
}

export function extractOrganizationLogoDataUrl(
  org: fhir5.Organization,
): string | undefined {
  const extensions = org.extension ?? [];
  const logoExt = extensions.find((ext) =>
    ext.url?.includes("OrganizationLogo"),
  );
  const attachment = logoExt?.valueAttachment;
  if (!attachment?.contentType || !attachment?.data) return undefined;
  return `data:${attachment.contentType};base64,${attachment.data}`;
}

export function resolveOrganizationDisplayName(org: fhir5.Organization): string {
  const alias = org.alias?.[0];
  if (alias && alias !== "null") return alias;
  return org.name?.trim() || org.id || "Dispensador";
}

export function extractInstitutionId(organizationId: string): string | null {
  const trimmed = organizationId.trim();
  if (!trimmed) return null;
  const parts = trimmed.split("-");
  if (parts.length < 2) return trimmed;
  return parts[1] || null;
}

export type MarketplaceDispensaryConfig = WooStoreConfig & {
  organizationId: string;
  institutionId: string;
  status: "active" | "coming_soon";
};

export function resolveStoreConfigFromOrganization(
  org: fhir5.Organization,
): MarketplaceDispensaryConfig | null {
  if (!org.id) return null;

  const institutionId = extractInstitutionId(org.id);
  if (!institutionId) return null;

  const displayName = resolveOrganizationDisplayName(org);
  const logoDataUrl = extractOrganizationLogoDataUrl(org);
  const registryEntry = PARSED_REGISTRY.registry[institutionId];

  if (!registryEntry) {
    return {
      id: institutionId,
      name: displayName,
      organizationId: org.id,
      institutionId,
      status: "coming_soon",
      active: false,
      logoDataUrl,
      logoUrl: logoDataUrl,
    };
  }

  const siteOrigin = resolveSiteOrigin(registryEntry);

  if (!siteOrigin) {
    return {
      id: registryEntry.storeSlug?.trim() || institutionId,
      name: displayName,
      organizationId: org.id,
      institutionId,
      status: "coming_soon",
      active: false,
      integrationError: `Dispensador "${displayName}" sem siteOrigin em VITE_WOO_ORGANIZATION_STORES (CNPJ ${institutionId}).`,
      logoDataUrl,
      logoUrl: logoDataUrl,
    };
  }

  return {
    id: registryEntry.storeSlug?.trim() || institutionId,
    name: displayName,
    organizationId: org.id,
    institutionId,
    status: "active",
    active: true,
    siteOrigin,
    logoDataUrl,
    logoUrl: logoDataUrl,
  };
}

export function isStoreApiReady(store: WooStoreConfig): boolean {
  return (
    store.status !== "coming_soon" &&
    store.active !== false &&
    Boolean(store.institutionId?.trim()) &&
    Boolean(store.siteOrigin?.trim())
  );
}

export { ORGANIZATION_LOGO_URL };
